// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type Context as ReactContext,
  type FC,
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  type ComponentType,
  type Consumer,
} from 'react';
import { EMPTY_VALUE } from '@shared/ui-kit/utils/constants';

export interface DataProviderOptions<
  Name extends string,
  ContextValue,
  ReactHook extends ((arg: {}) => ContextValue) | (() => ContextValue),
  DataValue = ContextValue,
> {
  readonly name: Name;
  readonly factory: ReactHook;
  readonly exports?: 'all' | ((data: ContextValue) => DataValue);
}

export type DataProviderFactory<
  Name extends string,
  Args extends void | {},
  ContextValue,
  DataValue = ContextValue,
> = {
  [K in `use${Name}Data`]: () => DataValue;
} & {
  [K in `use${Name}Data`]: <T = DataValue>() => T;
} & {
  [K in `${Name}Provider`]: FC<PropsWithChildren<Args extends void ? {} : Args>>;
} & {
  [K in `${Name}Consumer`]: Consumer<DataValue>;
} & {
  [K in `${Name}DataContext`]: ReactContext<DataValue>;
} & {
  [K in `with${Name}`]: (
    Component: ComponentType,
  ) => ComponentType<Args extends void ? {} : Args>;
};

export const createDataProvider = <
  Name extends Capitalize<string>,
  Args extends void | {},
  ContextValue,
  DataValue = ContextValue,
>({
  name,
  factory: useFactory,
  exports = 'all',
}: DataProviderOptions<
  Name,
  ContextValue,
  Args extends void ? () => ContextValue : (args: Args) => ContextValue,
  DataValue
>) => {
  const transformData = (data: ContextValue): DataValue => {
    if (exports === 'all') {
      return data as unknown as DataValue;
    }

    return exports(data);
  };

  const Context = createContext<DataValue>(EMPTY_VALUE as unknown as DataValue);

  Context.displayName = `${name}DataContext`;

  const Consumer = Context.Consumer;

  const Provider: FC<PropsWithChildren<Args extends void ? {} : Args>> = ({
    children,
    ...props
  }) => {
    const data = useFactory(props);

    const value = useMemo(() => transformData(data), [data]);

    return <Context.Provider value={value}>{children}</Context.Provider>;
  };

  Provider.displayName = `${name}DataProvider`;

  const useProvider = () => {
    const context = useContext(Context);

    if (context === (EMPTY_VALUE as unknown as DataValue)) {
      throw new Error(
        `could not find ${name} context value; please ensure the component is wrapped in a <${name}DataProvider>`,
      );
    }

    return context as DataValue;
  };

  const withProvider = (Component: ComponentType) => {
    const WrappedComponent: FC<Args extends void ? {} : Args> = props => {
      return (
        <Provider {...props}>
          <Component />
        </Provider>
      );
    };

    WrappedComponent.displayName = `${name}DataProvider(${Component.displayName})`;

    return WrappedComponent;
  };

  return {
    [`${name}DataContext`]: Context,
    [`use${name}Data`]: useProvider,
    [`${name}Provider`]: Provider,
    [`${name}Consumer`]: Consumer,
    [`with${name}`]: withProvider,
  } as DataProviderFactory<Name, Args, ContextValue, DataValue>;
};
